import { JSX } from "preact";
import { useState, useEffect } from "preact/hooks";

// Common country codes with flags - we can expand this list as needed
const COUNTRY_CODES = [
  { code: "1", label: "United States", flag: "🇺🇸", dialCode: "+1" },
  { code: "1", label: "Canada", flag: "🇨🇦", dialCode: "+1" },
  { code: "44", label: "United Kingdom", flag: "🇬🇧", dialCode: "+44" },
  { code: "61", label: "Australia", flag: "🇦🇺", dialCode: "+61" },
  { code: "64", label: "New Zealand", flag: "🇳🇿", dialCode: "+64" },
  { code: "52", label: "Mexico", flag: "🇲🇽", dialCode: "+52" },
  { code: "86", label: "China", flag: "🇨🇳", dialCode: "+86" },
  { code: "81", label: "Japan", flag: "🇯🇵", dialCode: "+81" },
  { code: "82", label: "South Korea", flag: "🇰🇷", dialCode: "+82" },
  { code: "91", label: "India", flag: "🇮🇳", dialCode: "+91" },
  { code: "55", label: "Brazil", flag: "🇧🇷", dialCode: "+55" },
] as const;

// Add a unique identifier for each country since codes can be shared
type CountryCode = (typeof COUNTRY_CODES)[number];
const getCountryKey = (country: CountryCode) =>
  `${country.code}-${country.label}`;

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isValid?: boolean;
  isDirty?: boolean;
  disabled?: boolean;
  className?: string;
  errorMessage?: string;
}

export const PhoneInput = ({
  value,
  onChange,
  onBlur,
  isValid = true,
  isDirty = false,
  disabled = false,
  className = "",
  errorMessage,
}: PhoneInputProps) => {
  // Extract country code and number from value
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    COUNTRY_CODES[0]
  );
  const [phoneNumber, setPhoneNumber] = useState("");

  // When the component mounts or value changes externally,
  // parse the value into country code and number
  useEffect(() => {
    if (value.startsWith("+")) {
      // Find the matching country code
      const matchingCode = COUNTRY_CODES.find((cc) =>
        value.startsWith(`+${cc.code}`)
      );
      if (matchingCode) {
        setSelectedCountry(matchingCode);
        setPhoneNumber(value.slice(matchingCode.code.length + 1)); // +1 for the + symbol
      }
    } else {
      setPhoneNumber(value);
    }
  }, [value]);

  // When either country code or number changes, combine them
  const handlePhoneChange = (e: JSX.TargetedEvent<HTMLInputElement>) => {
    const newNumber = (e.target as HTMLInputElement).value.replace(/\D/g, "");
    setPhoneNumber(newNumber);
    onChange(`+${selectedCountry.code}${newNumber}`);
  };

  const handleCountryChange = (e: JSX.TargetedEvent<HTMLSelectElement>) => {
    const countryKey = (e.target as HTMLSelectElement).value;
    const newCountry =
      COUNTRY_CODES.find((c) => getCountryKey(c) === countryKey) ||
      COUNTRY_CODES[0];
    setSelectedCountry(newCountry);
    onChange(`+${newCountry.code}${phoneNumber}`);
  };

  // Only show validation styles if the field is dirty and has a value
  const showValidation = isDirty && phoneNumber.length > 0;

  return (
    <div className="space-y-1">
      <div className="flex">
        <div className="relative">
          <select
            value={getCountryKey(selectedCountry)}
            onChange={handleCountryChange}
            disabled={disabled}
            className={`pl-8 pr-10 border-b border-r-0 p-2 focus:outline-none transition-colors bg-transparent appearance-none ${
              showValidation
                ? isValid
                  ? "border-green-500 focus:border-green-700"
                  : "border-red-500 focus:border-red-700"
                : "border-gray-300 focus:border-black"
            }`}
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              minWidth: "180px",
            }}
          >
            {COUNTRY_CODES.map((country) => (
              <option
                key={getCountryKey(country)}
                value={getCountryKey(country)}
              >
                {country.label} ({country.dialCode})
              </option>
            ))}
          </select>
          <div
            className="absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
            style={{ fontSize: "1.2em" }}
          >
            {selectedCountry.flag}
          </div>
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500">
            ▼
          </div>
        </div>
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          onBlur={onBlur}
          placeholder="555-555-5555"
          disabled={disabled}
          className={`flex-1 border-b border-l-0 p-2 focus:outline-none transition-colors ${className} ${
            showValidation
              ? isValid
                ? "border-green-500 focus:border-green-700"
                : "border-red-500 focus:border-red-700"
              : "border-gray-300 focus:border-black"
          }`}
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        />
      </div>
      {showValidation && !isValid && errorMessage && (
        <p className="text-red-600 text-sm mt-1">{errorMessage}</p>
      )}
    </div>
  );
};
